import React, { useEffect, useState, useRef, useCallback } from "react";
import "../SynthPlayer.css";
import { Container, Row, Col, Breadcrumb } from "react-bootstrap";
import { useLocation, Link } from "react-router-dom";
import { blogDetailsApi } from "../redux";
import { connect } from "react-redux";
import { imgUrl } from "../redux/config";
import BlogCardML from "../common/blogCardML";
import { useTranslation } from "react-i18next";
import { usePathLanguage } from "../hooks/usePathLanguage";
import { Helmet } from "react-helmet";


const SynthAudioPlayer = ({ audioSrc, srtSrc, contentSelector }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const requestRef = useRef();

  const [semanticWords, setSemanticWords] = useState([]);
  const scrollYRef = useRef(0);
  const targetScrollYRef = useRef(0);
  const highlightParamsRef = useRef({ x: 0, y: 0, w: 0, h: 32 });
  const targetHighlightRef = useRef({ x: 0, y: 0, w: 0, h: 32 });

  const currentActiveIdxRef = useRef(-1);
  const [hasSrt, setHasSrt] = useState(false);


  const timeToSeconds = (time) => {
    const p = time.split(':');
    const s = p[2].split(',');
    return parseInt(p[0]) * 3600 + parseInt(p[1]) * 60 + parseInt(s[0]) + parseInt(s[1]) / 1000;
  };

  const parseSRT = (data) => {
    const srt = data.replace(/\r/g, '');
    const regex = /(\d+)\n(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})\n([\s\S]*?)(?=\n\d+\n|\n*$)/g;
    let match, result = [];
    while ((match = regex.exec(srt)) !== null) {
      result.push({
        start: timeToSeconds(match[2]),
        end: timeToSeconds(match[3]),
        text: match[4].replace(/\n/g, ' ').trim()
      });
    }
    return result;
  };

  const alignSRT = (words, srt) => {
    const srtWords = [];
    srt.forEach((seg, segIdx) => {
      const segWords = seg.text.split(/\s+/);
      const dur = (seg.end - seg.start) / Math.max(1, segWords.length);
      segWords.forEach((txt, i) => {
        srtWords.push({
          clean: txt.toLowerCase().replace(/[^\w]/g, ''),
          start: seg.start + (i * dur),
          end: seg.start + ((i + 1) * dur),
          segStart: seg.start,
          segEnd: seg.end,
          segIdx: segIdx
        });
      });
    });

    let srtIdx = 0;
    words.forEach(sem => {
      if (sem.type === 'BREAK') return;
      if (srtIdx < srtWords.length) {
        sem.start = srtWords[srtIdx].start;
        sem.end = srtWords[srtIdx].end;
        sem.segStart = srtWords[srtIdx].segStart;
        sem.segEnd = srtWords[srtIdx].segEnd;
        sem.srtSegIdx = srtWords[srtIdx].segIdx;
        srtIdx++;
      }
    });
    return words;
  };



  useEffect(() => {
    console.log("Audio Source Path:", audioSrc);
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [audioSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  useEffect(() => {
    const checkSrt = async () => {
      // If srtSrc is explicitly provided, assume it's available
      if (srtSrc) {
        setHasSrt(true);
        return;
      }

      // If no srtSrc but we have audioSrc, try to auto-detect a .srt file
      if (audioSrc) {
        try {
          const autoSrtUrl = audioSrc.replace(/\.mp3$/i, '.srt');
          const res = await fetch(autoSrtUrl + '?t=' + Date.now());
          setHasSrt(res.ok);
        } catch (e) {
          setHasSrt(false);
        }
      } else {
        setHasSrt(false);
      }
    };
    checkSrt();
  }, [audioSrc, srtSrc]);

  const togglePlay = () => {
    if (audioRef.current.paused) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  };

  const handleProgressChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const parseContent = () => {
    const roots = document.querySelectorAll(contentSelector);
    if (roots.length === 0) return [];

    const words = [];

    function processNode(node, context) {
      const nextContext = { ...context };
      const tag = node.nodeName;

      if (tag === "H1") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 40;
        nextContext.fontWeight = "800";
        nextContext.lineHeight = 52;
        nextContext.marginTop = 100;
        nextContext.isHeading = true;
        nextContext.isUppercase = true;
      } else if (tag === "H2") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 32;
        nextContext.fontWeight = "700";
        nextContext.lineHeight = 42;
        nextContext.marginTop = 80;
        nextContext.isHeading = true;
      } else if (tag === "H3") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 26;
        nextContext.fontWeight = "700";
        nextContext.lineHeight = 36;
        nextContext.marginTop = 60;
        nextContext.isHeading = true;
      } else if (tag === "H4") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 20;
        nextContext.fontWeight = "700";
        nextContext.lineHeight = 30;
        nextContext.marginTop = 50;
        nextContext.isHeading = true;
      } else if (tag === "P") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 20;
        nextContext.fontWeight = "400";
        nextContext.lineHeight = 36;
        nextContext.marginTop = 30;
      } else if (tag === "LI") {
        nextContext.fontFamily = '"Open Sans", sans-serif';
        nextContext.fontSize = 20;
        nextContext.lineHeight = 32;
        nextContext.marginTop = 15;
        nextContext.isBullet = true;
      } else if (["STRONG", "B", "A"].includes(tag)) {
        nextContext.isBold = true;
      } else if (['EM', 'I'].includes(tag)) {
        nextContext.isItalic = true;
      } else if (tag === 'U') {
        nextContext.isUnderline = true;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const rawText = node.textContent.trim();
        if (rawText) {
          rawText.split(/\s+/).forEach(word => {
            const text = nextContext.isUppercase ? word.toUpperCase() : word;
            words.push({ text: text, context: { ...nextContext } });
          });
        }
      } else {
        const isBlock = ["H1", "H2", "H3", "H4", "P", "LI", "DIV", "SECTION", "ARTICLE", "UL", "OL", "BR"].includes(tag);
        if (isBlock && words.length > 0) {
          const lastWord = words[words.length - 1];
          const mTop = nextContext.marginTop || 30;
          if (lastWord.type === "BREAK") {
            lastWord.margin = Math.max(lastWord.margin || 0, mTop);
            if (tag === "LI") lastWord.isBullet = true;
          } else {
            words.push({
              type: "BREAK",
              margin: mTop,
              isBullet: tag === "LI",
            });
          }
        }

        node.childNodes.forEach((child) => processNode(child, nextContext));

        if (isBlock && words.length > 0 && words[words.length - 1].type !== "BREAK") {
          words.push({ type: "BREAK", margin: 20 });
        }
      }
    }

    roots.forEach(root => {
      processNode(root, {
        fontFamily: '"Open Sans", sans-serif',
        fontSize: 18,
        fontWeight: '400',
        lineHeight: 36,
        isHeading: false
      });
    });

    // Fallback if no words found
    if (words.length === 0) {
      words.push({ text: "Searching for blog content...", context: { fontSize: 24, fontWeight: 'bold', marginTop: 100, fontFamily: '"Open Sans", sans-serif' } });
    }

    // Default timings (fallback)
    const wordsPerSec = 2.5;
    let wordCounter = 0;
    words.forEach((w) => {
      if (w.type !== 'BREAK') {
        w.start = wordCounter / wordsPerSec;
        w.end = (wordCounter + 1) / wordsPerSec;
        wordCounter++;
      }
    });

    return words;
  };



  const openImmersive = async () => {
    let words = parseContent();

    // Try to load SRT if available
    try {
      const srtUrl = srtSrc || audioSrc.replace(/\.mp3$/i, '.srt');
      console.log("SRT Source Path:", srtUrl);
      const res = await fetch(srtUrl + '?v=' + Date.now());
      if (res.ok) {
        const data = await res.text();
        const parsedSrt = parseSRT(data);
        words = alignSRT(words, parsedSrt);
      }
    } catch (e) {
      console.warn("SRT Load Error:", e);
    }

    setSemanticWords(words);
    setShowModal(true);
    if (audioRef.current.paused) audioRef.current.play();
  };


  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const audio = audioRef.current;

    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);

    const winW = canvas.width / dpr;
    const winH = canvas.height / dpr;
    const isMobile = winW < 768;
    const paddingX = isMobile ? 30 : 100;
    const maxWidth = winW - (paddingX * 2);

    scrollYRef.current += (targetScrollYRef.current - scrollYRef.current) * 0.15;
    const h = highlightParamsRef.current;
    h.x += (targetHighlightRef.current.x - h.x) * 0.25;
    h.y += (targetHighlightRef.current.y - h.y) * 0.25;
    h.w += (targetHighlightRef.current.w - h.w) * 0.25;
    h.h += (targetHighlightRef.current.h - h.h) * 0.25;

    let curX = paddingX;
    let curY = 150;
    let activeIdx = -1;
    // Add a subtle 150ms lead - usually the sweet spot for visual/audio sync
    const time = audio.currentTime + 0.15;


    semanticWords.forEach((word, i) => {
      if (word.type === 'BREAK') {
        curX = paddingX;
        curY += word.margin || 20;
        return;
      }

      const c = word.context;
      let fs = c.fontSize || 18;
      if (isMobile) {
        if (c.isHeading) fs = Math.max(18, fs * 0.8);
        else fs = 16;
      }

      const weight = c.isBold ? 'bold' : (c.fontWeight || '400');
      ctx.font = `${c.isItalic ? 'italic ' : ''}${weight} ${fs}px ${c.fontFamily}`;
      const metrics = ctx.measureText(word.text + ' ');

      if (curX + metrics.width > maxWidth + paddingX) {
        curX = paddingX;
        curY += (isMobile ? fs * 1.5 : (c.lineHeight || 36));
      }

      word.x = curX;
      word.y = curY;
      word.w = metrics.width;
      word.h = fs;

      if (time >= word.start) activeIdx = i;
      curX += metrics.width;
    });

    currentActiveIdxRef.current = activeIdx;

    ctx.save();
    ctx.translate(0, scrollYRef.current);

    if (activeIdx !== -1) {
      const activeWord = semanticWords[activeIdx];
      const activeSeg = activeWord.srtSegIdx;

      // Group words in the same SRT segment by line
      let segWords;
      if (activeSeg !== undefined && activeSeg !== null) {
        segWords = semanticWords.filter(w => w.type !== 'BREAK' && w.srtSegIdx === activeSeg);
      } else {
        // Fallback block detection
        let start = activeIdx;
        let end = activeIdx;
        while (start > 0 && semanticWords[start - 1].type !== 'BREAK') start--;
        while (end < semanticWords.length - 1 && semanticWords[end + 1].type !== 'BREAK') end++;
        segWords = semanticWords.slice(start, end + 1).filter(w => w.type !== 'BREAK');
      }

      // Group by line with a small tolerance for jitter
      const lineMap = {};
      segWords.forEach(w => {
        // Use a 10px grouping tolerance to prevent line merging/splitting issues
        const lineKey = Math.round(w.y / 10) * 10;
        if (!lineMap[lineKey]) {
          lineMap[lineKey] = { x: w.x, endX: w.x + w.w, y: w.y, h: w.h };
        } else {
          lineMap[lineKey].x = Math.min(lineMap[lineKey].x, w.x);
          lineMap[lineKey].endX = Math.max(lineMap[lineKey].endX, w.x + w.w);
        }
      });

      const lines = Object.values(lineMap);

      // Use the active word's line for scroll targeting
      const newScroll = (winH / (isMobile ? 3 : 3.5)) - activeWord.y;

      // Detect section change: if the word right before the active word is a BREAK, snap instantly
      const isNewSection = activeIdx > 0 && semanticWords[activeIdx - 1].type === "BREAK";
      if (isNewSection && currentActiveIdxRef.current !== activeIdx) {
        scrollYRef.current = newScroll;
      }

      targetScrollYRef.current = newScroll;

      // Draw highlight rectangles for each line in this SRT segment
      lines.forEach(line => {
        const lineW = line.endX - line.x;
        const lineH = line.h * 1.5;
        const blur = lineW * 0.15;
        const grad = ctx.createLinearGradient(line.x - blur, 0, line.endX + blur, 0);
        grad.addColorStop(0, "rgba(255, 255, 0, 0)");
        grad.addColorStop(0.1, "rgba(255, 255, 0, 0.55)");
        grad.addColorStop(0.9, "rgba(255, 255, 0, 0.55)");
        grad.addColorStop(1, "rgba(255, 255, 0, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(line.x - blur, line.y - lineH * 0.8, lineW + blur * 2, lineH);
      });
    }

    // Draw all words normally
    semanticWords.forEach((word, index) => {
      if (word.type === 'BREAK') {
        if (word.isBullet) {
          ctx.fillStyle = '#0f172a';
          ctx.font = `400 ${isMobile ? 16 : 18}px "Open Sans", sans-serif`;
          ctx.fillText('•', word.x - (isMobile ? 20 : 35), word.y);
        }
        return;
      }

      const c = word.context;
      const weight = c.isBold ? 'bold' : (word.context.fontWeight || '400');
      ctx.font = `${c.isItalic ? 'italic ' : ''}${weight} ${word.h}px ${c.fontFamily}`;

      ctx.fillStyle = "#111111";

      ctx.fillText(word.text, word.x, word.y);
      if (c.isUnderline) {
        ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 1; ctx.beginPath();
        ctx.moveTo(word.x, word.y + 4); ctx.lineTo(word.x + word.w - 5, word.y + 4); ctx.stroke();
      }
    });

    ctx.restore();

    requestRef.current = requestAnimationFrame(render);
  }, [semanticWords]);


  useEffect(() => {
    if (showModal) {
      const resize = () => {
        if (!canvasRef.current) return;
        canvasRef.current.width = window.innerWidth * (window.devicePixelRatio || 1);
        canvasRef.current.height = window.innerHeight * (window.devicePixelRatio || 1);
        canvasRef.current.style.width = window.innerWidth + 'px';
        canvasRef.current.style.height = window.innerHeight + 'px';
      };
      resize();
      window.addEventListener("resize", resize);
      requestRef.current = requestAnimationFrame(render);
      return () => {
        window.removeEventListener("resize", resize);
        cancelAnimationFrame(requestRef.current);
      };
    }
  }, [showModal, semanticWords, render]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = (e.clientX - rect.left);
    const clickY = (e.clientY - rect.top) - scrollYRef.current;

    const clickedWord = semanticWords.find(w => {
      if (w.type === 'BREAK') return false;
      return clickX >= (w.x - 10) &&
        clickX <= (w.x + w.w + 10) &&
        clickY >= (w.y - (w.h || 18) - 5) &&
        clickY <= (w.y + 10);
    });

    if (clickedWord && clickedWord.start !== undefined) {
      // Seek to the start of the SRT segment/phrase if available, otherwise to word start
      const seekTime = clickedWord.segStart !== undefined ? clickedWord.segStart : clickedWord.start;
      audioRef.current.currentTime = seekTime;
      if (audioRef.current.paused) audioRef.current.play();
    } else {
      togglePlay();
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  return (

    <div className="synth-player-container">

      <div className="synth-header-player">

        <button className="synth-play-btn" onClick={togglePlay}>
          {isPlaying ? (
            <svg viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
          ) : (
            <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>

        <div className="synth-progress-wrap">
          <div className="synth-progress-label">
            <span>{isPlaying ? "Reading Now" : "Listen to this Blog"}</span>
            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
          <div className="synth-progress-container" onClick={handleProgressChange}>
            <div
              className="synth-progress-bar"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            ></div>
          </div>
        </div>

        {hasSrt && (
          <div className="synth-actions">
            <button className="synth-btn primary" onClick={openImmersive}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" /></svg>
              Immersive Reading
            </button>
          </div>
        )}
      </div>

      <audio
        ref={audioRef}
        src={audioSrc}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        playbackRate={playbackRate}
      />

      {/* Immersive Modal */}
      <div className={`synth-modal ${showModal ? "active" : ""}`}>
        <button className="synth-modal-close" onClick={() => setShowModal(false)}>✕</button>

        <div className="synth-canvas-wrapper">
          <canvas ref={canvasRef} onMouseDown={handleCanvasClick} />

        </div>


        <div className="synth-modal-controls">
          <div className="synth-ctrl-group">
            <label>Current Time</label>
            <div className="synth-time-display">{formatTime(currentTime)}</div>
          </div>

          <div className="synth-ctrl-group">
            <label>Speed</label>
            <select
              className="synth-speed-select"
              value={playbackRate}
              onChange={(e) => {
                const rate = parseFloat(e.target.value);
                setPlaybackRate(rate);
                audioRef.current.playbackRate = rate;
              }}
            >
              <option value="0.5">0.5x</option>
              <option value="1">1.0x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2.0x</option>
            </select>
          </div>

          <div className="synth-ctrl-group">
            <label>Volume</label>
            <input
              type="range"
              className="synth-volume-range"
              min="0" max="1" step="0.1"
              value={volume}
              onChange={(e) => {
                const vol = parseFloat(e.target.value);
                setVolume(vol);
                audioRef.current.volume = vol;
              }}
            />
          </div>

          <div className="synth-ctrl-group synth-skip-group">
            <button className="synth-skip-btn" onClick={() => skipTime(-10)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" /></svg>
              10s
            </button>
            <button className="synth-skip-btn" onClick={() => skipTime(10)}>
              10s
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 17l5-5-5-5M6 17l5-5-5-5" /></svg>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};


const Blog = ({ getBlogDetails, blogdetails }) => {

  const location = useLocation();

  const { t } = useTranslation();
  const pathLanguage = usePathLanguage();
  //   useEffect(() => {
  //     async function fetchData() {
  //         var x = await location.pathname.split("/");
  //         blogDetailsApi(`${x[2]}`);
  //     }

  //     fetchData();
  // }, [location]);

  useEffect(() => {
    async function fetchData() {
      const x = location.pathname.split("/");
      const lang = ["hindi", "gujarati", "marathi"].includes(x[1]) ? x[1] : "en";
      const page_url = lang === "en" ? x[x.length - 1] : x[3];

      if (page_url) {
        await getBlogDetails(page_url, lang);
      }
    }
    fetchData();
  }, [location, getBlogDetails]);


  // if (Object.keys(blogdetails).length) {
  //   IsData = true;
  //   var data = { ...blogdetails.data };
  //   var blog = data.detail[0];

  //   console.log(data);
  // }
  let blog = null;
  let data = {};
  let IsData = false;

  if (blogdetails && Object.keys(blogdetails).length) {
    IsData = true;
    data = { ...blogdetails.data };

    // ✅ handle both object or array API response
    if (Array.isArray(data.detail)) {
      blog = data.detail[0];
      // alert(blog.blog_title)
    } else {
      blog = data.detail;
    }
  }
  let title = "";
  let desc = "";
  if (pathLanguage.toString().includes("hindi") && blog?.blog_title_hin) {
    title = blog?.blog_title_hin;
    desc = blog?.meta_description_hin;
  } else if (
    pathLanguage.toString().includes("gujarati") &&
    blog?.blog_title_guj
  ) {
    title = blog?.blog_title_guj;
    desc = blog?.meta_description_guj;
  } else if (
    pathLanguage.toString().includes("marathi") &&
    blog?.blog_title_mar
  ) {
    title = blog?.blog_title_mar;
    desc = blog?.meta_description_mar;
  } else {
    title = blog?.blog_title;
    desc = blog?.meta_description;
  }

  let languages = [];

  if (blog?.blog_title) {
    languages.push({
      code: "en",
      url: `/${blog?.page_url}`,
    });
  }

  if (blog?.blog_title_hin) {
    languages.push({
      code: "hi",
      url: `/hindi/${blog?.page_url_hin || blog?.page_url}`,
    });
  }

  if (blog?.blog_title_guj) {
    languages.push({
      code: "gu",
      url: `/gujarati/${blog?.page_url_guj || blog?.page_url}`,
    });
  }
  if (blog?.blog_title_mar) {
    languages.push({
      code: "mr",
      url: `/marathi/${blog?.page_url_mar || blog?.page_url}`,
    });
  }
  console.log(blog);
  return (
    <div className="media inner_page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />

        <link
          rel="canonical"
          href={window.location.origin + window.location.pathname}
        />
        {languages.map((lang) => (
          <link
            key={lang.code}
            rel="alternate"
            hreflang={lang.code}
            href={window.location.origin + lang.url}
          />
        ))}
        <link
          rel="alternate"
          hreflang="x-default"
          href={`${window.location.origin + "/" + blog?.page_url}`}
        />
      </Helmet>
      {(IsData && (
        <>
          <div className="banner text-center position-relative">
            <img
              src={
                imgUrl +
                ((pathLanguage.toString().includes("hindi") &&
                  blog?.banner_hin_img) ||
                  (pathLanguage.toString().includes("gujarati") &&
                    blog?.banner_guj_img) ||
                  (pathLanguage.toString().includes("marathi") &&
                    blog?.banner_mar_img) ||
                  blog?.banner_img)
              }
              alt=""
            />
            <article className="position-absolute text-center">
              <Container>
                {/* <h1 className="text-uppercase">{blog.blog_title}</h1>
                 */}
                <h1 className="text-uppercase">
                  {pathLanguage.toString().includes("hindi") &&
                    blog?.blog_title_hin}
                  {pathLanguage.toString().includes("gujarati") &&
                    blog?.blog_title_guj}
                  {pathLanguage.toString().includes("marathi") &&
                    blog?.blog_title_mar}
                  {!pathLanguage.toString().includes("gujarati") &&
                    !pathLanguage.toString().includes("hindi") &&
                    !pathLanguage.toString().includes("marathi") &&
                    blog?.blog_title}
                </h1>
              </Container>
            </article>
            <div className="breadouter">
              <div className="container">
                <Breadcrumb className="mb-0 py-2">
                  <Breadcrumb.Item as={Link} to={`${pathLanguage}media/blog`}>
                    {t("media.Blogs")}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>
                    {pathLanguage.toString().includes("hindi") &&
                      blog?.blog_title_hin}
                    {pathLanguage.toString().includes("gujarati") &&
                      blog?.blog_title_guj}
                    {pathLanguage.toString().includes("marathi") &&
                      blog?.blog_title_mar}
                    {!pathLanguage.toString().includes("gujarati") &&
                      !pathLanguage.toString().includes("hindi") &&
                      !pathLanguage.toString().includes("marathi") &&
                      blog?.blog_title}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </div>
            </div>
          </div>




          <div className="midpart mt-5">
            <Container>
              {/* ── Synth MP3 Audio Player ── */}
              {blog?.audio_file && (
                <SynthAudioPlayer
                  audioSrc={imgUrl + blog.audio_file}
                  srtSrc={blog.srt_file ? imgUrl + blog.srt_file : null}
                  contentSelector=".banner h1, .midpart .container > div:not(.blog):not(.synth-player-container)"
                />

              )}

              {pathLanguage.toString().includes("hindi") && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: blog?.banner_desc_hin || "",
                  }}
                ></div>
              )}
              {pathLanguage.toString().includes("gujarati") && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: blog?.banner_desc_guj || "",
                  }}
                ></div>
              )}
              {pathLanguage.toString().includes("marathi") && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: blog?.banner_desc_mar || "",
                  }}
                ></div>
              )}

              {!pathLanguage.toString().includes("gujarati") &&
                !pathLanguage.toString().includes("hindi") &&
                !pathLanguage.toString().includes("marathi") && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: blog?.banner_desc || "",
                    }}
                  ></div>
                )}

              <div className="blog card_round blog_details mt-5">
                <Row>
                  {data.latestBlog?.map((slide, index) => {
                    return (
                      <Col md={4} key={index}>
                        <BlogCardML item={slide} id={4} uri={imgUrl} />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </Container>
          </div>
        </>
      )) ||
        ""}
    </div>
  );
};

const mapStatetoProps = (state) => {
  return {
    blogdetails: state.mediaR.blogdetails,
  };
};

const mapDispatchtoProps = (dispatch) => {

  return {
    getBlogDetails: function (page_url, pathLanguage) {
      dispatch(blogDetailsApi(page_url, pathLanguage));
    },
  };

};

export default connect(mapStatetoProps, mapDispatchtoProps)(Blog);